import { SearchIcon } from "@chakra-ui/icons";
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import React from "react";
import EntityModalButton from "../../entityProfileModal/components/EntityModalButton";
import {
  type AiCapabilitySetting,
  getAiCapabilitySettings,
  subscribeToAiCapabilities,
} from "../ai-capability-registry";
import {
  setAiCapabilityEnabled,
  subscribeToAiCapabilityPreferences,
} from "../ai-capability-preferences.store";

type Props = {
  onClose: () => void;
};

type Row = AiCapabilitySetting & { depth: number; isReachable: boolean };

/**
 * Parents first, each followed by what sits behind it.
 *
 * A child is only reachable while its parent is: the AI cannot open a patient's notes without
 * opening the patient, so the row is shown switched off and cannot be set until the parent is back
 * on. Its own preference is kept, so turning the parent on restores what the child was.
 */
function toRows(
  settings: AiCapabilitySetting[],
  matches: (setting: AiCapabilitySetting) => boolean
): Row[] {
  const children = new Map<string, AiCapabilitySetting[]>();

  for (const setting of settings) {
    if (setting.parentId !== null) {
      children.set(setting.parentId, [...(children.get(setting.parentId) ?? []), setting]);
    }
  }

  const rows: Row[] = [];

  for (const parent of settings.filter((setting) => setting.parentId === null)) {
    const mine = children.get(parent.id) ?? [];
    const matching = mine.filter(matches);
    // A parent is kept when it matches, and also when one of its children does - a child with no
    // row to sit under would read as a top-level way in, which it is not.
    if (!matches(parent) && matching.length === 0) {
      continue;
    }

    rows.push({ ...parent, depth: 0, isReachable: true });

    for (const child of matches(parent) ? mine : matching) {
      rows.push({ ...child, depth: 1, isReachable: parent.isEnabled });
    }
  }

  return rows;
}

/**
 * What the AI is allowed to reach, as a list you can switch off one at a time.
 *
 * Only what this role may use is listed: the capabilities are permission-filtered before they reach
 * here, so the panel never offers a switch that changes nothing.
 */
export default function AiSettingsModal(props: Props) {
  const [, forceUpdate] = React.useReducer((count: number) => count + 1, 0);
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    const releases = [
      subscribeToAiCapabilities(forceUpdate),
      subscribeToAiCapabilityPreferences(forceUpdate),
    ];

    return () => releases.forEach((release) => release());
  }, []);

  const settings = getAiCapabilitySettings();
  const term = search.trim().toLowerCase();
  const matches = (setting: AiCapabilitySetting) =>
    term === "" || setting.title.toLowerCase().includes(term);

  const rows = toRows(settings, matches);
  const enabledCount = settings.filter((setting) => setting.isEnabled).length;

  // The bulk buttons act on what is on screen, which is the only reading of them that is not a
  // surprise when a search is in the box.
  const setAllShown = (isEnabled: boolean) =>
    rows.forEach((row) => setAiCapabilityEnabled(row.id, isEnabled));

  return (
    <Modal isOpen scrollBehavior="inside" size="lg" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="18px">AI search settings</ModalHeader>
        <Divider />
        <ModalCloseButton onClick={props.onClose} />
        <ModalBody>
          <Flex direction="column" gap={4} py={2}>
            <Text color="gray.600" fontSize="13px">
              {enabledCount} of {settings.length} switched on. Switching one off also takes away
              what the AI could do once there, so it cannot act on a page it may not open. This
              applies to your browser only.
            </Text>

            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                borderRadius="md"
                placeholder="Search settings"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </InputGroup>

            <Flex align="center" gap={3}>
              <Button size="xs" variant="link" onClick={() => setAllShown(true)}>
                Select all
              </Button>
              <Button size="xs" variant="link" onClick={() => setAllShown(false)}>
                Deselect all
              </Button>
              {term === "" ? null : (
                <Text color="gray.500" fontSize="12px">
                  {rows.length} shown
                </Text>
              )}
            </Flex>

            <Divider />

            {rows.length === 0 ? (
              <Text color="gray.500" fontSize="13px">
                Nothing matches &ldquo;{search.trim()}&rdquo;.
              </Text>
            ) : (
              <Flex direction="column" gap={2}>
                {rows.map((row) => (
                  <Checkbox
                    key={row.id}
                    isChecked={row.isEnabled && row.isReachable}
                    isDisabled={!row.isReachable}
                    pl={row.depth * 6}
                    onChange={(event) => setAiCapabilityEnabled(row.id, event.target.checked)}
                  >
                    <Text color={row.isReachable ? undefined : "gray.400"} fontSize="14px">
                      {row.title}
                    </Text>
                  </Checkbox>
                ))}
              </Flex>
            )}
          </Flex>
        </ModalBody>
        <Divider />
        <ModalFooter>
          <EntityModalButton kind="blue" size="sm" onClick={props.onClose}>
            Done
          </EntityModalButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
